import React, { useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { useTopicFilters } from '../../../../contexts/TopicFiltersContext';

/**
 * TaskSearchInput component
 * 
 * Search input component that uses TopicFiltersContext.
 * Must be used within a TopicFiltersProvider.
 * 
 * Usage:
 * <TaskSearchInput />
 */
interface TaskSearchInputProps {
  isMobile?: boolean;
  placeholder?: string;
  className?: string;
}

export const TaskSearchInput: React.FC<TaskSearchInputProps> = ({
  isMobile = false,
  placeholder = 'Search',
  className,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  // Get search state from context - clean and simple!
  const { searchQuery, setSearchQuery } = useTopicFilters();

  const inputRef = useRef<HTMLInputElement>(null);

  // Clear search query
  const handleClearSearch = () => {
    setSearchQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Update search query when input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Check if search is active
  const hasActiveSearch = !!searchQuery?.trim();

  // Handle click on the search button/area
  const handleSearchClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div
      onClick={handleSearchClick}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      className={cn(
        'px-3 py-1.5 rounded-full text-sm flex items-center gap-2',
        'transition-all duration-300 ease-in-out', // Improved transition
        hasActiveSearch || isFocused
          ? 'bg-white text-blue-700 shadow-sm'
          : 'text-gray-700 hover:text-blue-700 hover:bg-white hover:shadow-sm',
        className
      )}
    >
      <Search size={16} className="text-blue-500" />

      <input
        ref={inputRef}
        type="text"
        value={searchQuery}
        onChange={handleSearchChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        className={cn(
          'bg-transparent border-none focus:outline-none focus:ring-0',
          'text-sm h-5 leading-none placeholder-gray-700',
          'transition-all duration-300 ease-in-out', // Add smooth transition with easing
          (hasActiveSearch || isFocused) ? 'text-blue-700' : 'text-gray-700',
          isMobile
            ? 'w-12'
            : (isHovered || isFocused) ? 'w-32' : 'w-12' // Slightly wider for better visibility
        )}
      />

      <div className="relative">
        {hasActiveSearch && (
          <X
            size={14}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-blue-400 hover:text-blue-600 transition-colors cursor-pointer"
            onClick={e => {
              e.stopPropagation();
              handleClearSearch();
            }}
            aria-label="Clear search"
          />
        )}
      </div>
    </div>
  );
};
